import { Users, Boxes, ClipboardList, CheckSquare, MessageSquare, FileText } from 'lucide-react'
import { SearchFilters } from '@/pages/search/SearchResultsPage'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'

interface SearchResultsFiltersProps {
  filters: SearchFilters
  onFiltersChange: (filters: Partial<SearchFilters>) => void
  className?: string
}

export function SearchResultsFilters({
  filters,
  onFiltersChange,
  className,
}: SearchResultsFiltersProps) {
  const nodeTypes = [
    { id: 'user', label: 'Users', icon: Users },
    { id: 'workspace', label: 'Workspaces', icon: Boxes },
    { id: 'project', label: 'Projects', icon: ClipboardList },
    { id: 'task', label: 'Tasks', icon: CheckSquare },
    { id: 'message', label: 'Messages', icon: MessageSquare },
    { id: 'document', label: 'Documents', icon: FileText },
  ] as const

  const getCurrentTypes = (): Array<
    'user' | 'workspace' | 'project' | 'task' | 'message' | 'document'
  > => {
    if (filters.types === '') return []
    try {
      return JSON.parse(filters.types) as Array<
        'user' | 'workspace' | 'project' | 'task' | 'message' | 'document'
      >
    } catch {
      return []
    }
  }

  const handleTypeChange = (type: string, checked: boolean) => {
    const currentTypes = getCurrentTypes()
    let newTypes: Array<'user' | 'workspace' | 'project' | 'task' | 'message' | 'document'>

    if (checked) {
      newTypes = [...currentTypes, type as (typeof currentTypes)[0]]
    } else {
      newTypes = currentTypes.filter((t) => t !== type)
    }

    onFiltersChange({ types: JSON.stringify(newTypes) })
  }

  const selectAll = () => {
    onFiltersChange({
      types: JSON.stringify(['user', 'workspace', 'project', 'task', 'message', 'document']),
    })
  }

  const clearAll = () => {
    onFiltersChange({ types: JSON.stringify([]) })
  }

  const selectedCount = getCurrentTypes().length

  return (
    <Card className={className}>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-medium">Filter by type</CardTitle>
          <div className="flex gap-2">
            <button
              onClick={selectAll}
              className="text-xs text-muted-foreground hover:text-foreground"
            >
              Select all
            </button>
            <button
              onClick={clearAll}
              className="text-xs text-muted-foreground hover:text-foreground"
            >
              Clear all
            </button>
          </div>
        </div>
        {selectedCount > 0 && (
          <Badge variant="secondary" className="text-xs w-fit">
            {selectedCount} selected
          </Badge>
        )}
      </CardHeader>
      <CardContent className="pt-0">
        <div className="space-y-3">
          {nodeTypes.map((type) => (
            <div key={type.id} className="flex items-center space-x-2">
              <Checkbox
                id={`filter-${type.id}`}
                checked={getCurrentTypes().includes(
                  type.id as 'user' | 'workspace' | 'project' | 'task' | 'message' | 'document'
                )}
                onCheckedChange={(checked) => handleTypeChange(type.id, checked as boolean)}
              />
              <Label
                htmlFor={`filter-${type.id}`}
                className="flex items-center gap-2 text-sm cursor-pointer"
              >
                <type.icon className="h-4 w-4" />
                {type.label}
              </Label>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  )
}
